import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import type { LookupItem } from '../../store/api/saleContractApi'
import {
  type SaleAmendment,
  type SaleAmendmentPayload,
  useCreateSaleAmendmentMutation,
  useGetSaleAmendmentQuery,
  useGetSaleAmendmentContractSnapshotQuery,
  useLazyGetSaleAmendmentContractsQuery,
  useUpdateSaleAmendmentMutation,
} from '../../store/api/saleAmendmentsApi'

interface SaleAmendmentFormDialogProps {
  open: boolean
  mode: 'new' | 'edit'
  amendmentNo?: string
  onClose: () => void
}

interface SaleAmendmentFormModel {
  amendmentNo: string
  amendmentDate: string
  contractNo: string
  contentBeforeChange: string
  contentAfterChange: string
  note: string
  price: number | ''
  quantity: number | ''
}

const today = () => new Date().toISOString().slice(0, 10)

const emptyForm = (): SaleAmendmentFormModel => ({
  amendmentNo: '',
  amendmentDate: today(),
  contractNo: '',
  contentBeforeChange: '',
  contentAfterChange: '',
  note: '',
  price: '',
  quantity: '',
})

const toNum = (v: number | ''): number | null => (v === '' ? null : v)

const safeDate = (v: unknown): string => {
  if (!v) return ''
  const s = String(v)
  return s.length >= 10 ? s.slice(0, 10) : s
}

const amendmentToForm = (a: SaleAmendment): SaleAmendmentFormModel => ({
  amendmentNo: a.amendmentNo,
  amendmentDate: safeDate(a.amendmentDate),
  contractNo: a.contractNo,
  contentBeforeChange: a.contentBeforeChange ?? '',
  contentAfterChange: a.contentAfterChange ?? '',
  note: a.note ?? '',
  price: a.price ?? '',
  quantity: a.quantity ?? '',
})

const formToPayload = (f: SaleAmendmentFormModel): SaleAmendmentPayload => ({
  amendmentNo: f.amendmentNo,
  amendmentDate: f.amendmentDate,
  contractNo: f.contractNo,
  contentBeforeChange: f.contentBeforeChange || null,
  contentAfterChange: f.contentAfterChange || null,
  note: f.note || null,
  price: toNum(f.price),
  quantity: toNum(f.quantity),
})

const extractApiErrorMessage = (error: unknown): string => {
  if (!error || typeof error !== 'object' || !('data' in error)) return 'Failed to save amendment. Please try again.'
  const data = (error as { data?: unknown }).data
  if (!data || typeof data !== 'object') return 'Failed to save amendment. Please try again.'
  const detail = (data as { detail?: unknown }).detail
  if (typeof detail === 'string' && detail.trim()) return detail
  if (Array.isArray(detail) && detail.length > 0) {
    const first = detail[0]
    if (typeof first === 'string') return first
    if (first && typeof first === 'object' && 'msg' in first) {
      const msg = (first as { msg?: unknown }).msg
      if (typeof msg === 'string') return msg
    }
    return JSON.stringify(first)
  }
  return 'Failed to save amendment. Please try again.'
}

const roSx = { '& .MuiInputBase-input': { color: 'text.secondary' } }

export default function SaleAmendmentFormDialog({ open, mode, amendmentNo, onClose }: SaleAmendmentFormDialogProps) {
  const [form, setForm] = useState<SaleAmendmentFormModel>(emptyForm)
  const [validationError, setValidationError] = useState<string | null>(null)
  const [fetchContracts, { data: contractOptions, isFetching: contractsLoading }] = useLazyGetSaleAmendmentContractsQuery()
  const { data: editData, isFetching: editLoading } = useGetSaleAmendmentQuery(amendmentNo ?? '', {
    skip: !open || mode !== 'edit' || !amendmentNo,
  })
  const { data: snapshot } = useGetSaleAmendmentContractSnapshotQuery(form.contractNo, {
    skip: !open || !form.contractNo,
  })
  const [createAmendment, { isLoading: creating }] = useCreateSaleAmendmentMutation()
  const [updateAmendment, { isLoading: updating }] = useUpdateSaleAmendmentMutation()
  const isBusy = creating || updating || editLoading

  const contractItems = useMemo(() => contractOptions?.items ?? [], [contractOptions])

  useEffect(() => {
    if (!open) return
    setValidationError(null)
    if (mode === 'new') {
      setForm(emptyForm())
      fetchContracts('')
    }
  }, [open, mode, fetchContracts])

  useEffect(() => {
    if (open && mode === 'edit' && editData) {
      setForm(amendmentToForm(editData))
    }
  }, [open, mode, editData])

  const set = <K extends keyof SaleAmendmentFormModel>(key: K, value: SaleAmendmentFormModel[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }))

  const setNum = (key: keyof SaleAmendmentFormModel, raw: string) =>
    setForm((prev) => ({ ...prev, [key]: raw === '' ? '' : Number(raw) }))

  const validateLikeAccess = (): string | null => {
    if (!form.contractNo.trim()) return 'Please choose Contract No!'
    if (!form.amendmentNo.trim()) return 'Please type Amendment No!'
    if (!form.amendmentDate) return 'Please choose Amendment Date!'
    return null
  }

  const handleSave = async () => {
    const accessError = validateLikeAccess()
    if (accessError) {
      setValidationError(accessError)
      return
    }
    setValidationError(null)
    try {
      const body = formToPayload(form)
      if (mode === 'edit' && amendmentNo) {
        await updateAmendment({ amendmentNo, body }).unwrap()
      } else {
        await createAmendment(body).unwrap()
      }
      onClose()
    } catch (error) {
      setValidationError(extractApiErrorMessage(error))
    }
  }

  const handleCancel = () => {
    setForm(emptyForm())
    setValidationError(null)
    onClose()
  }

  // Pull contract context from either the live snapshot query or the edit prefill
  const ctx = {
    vendor: snapshot?.contractClientName ?? editData?.contractSnapshot?.contractClientName ?? '',
    contractDate: safeDate(snapshot?.contractDate ?? editData?.contractSnapshot?.contractDate),
    contractType: snapshot?.contractType ?? editData?.contractSnapshot?.contractType ?? '',
    quality: snapshot?.contractQuality ?? editData?.contractSnapshot?.contractQuality ?? '',
    contractQty: snapshot?.contractQuantity ?? editData?.contractSnapshot?.contractQuantity ?? '',
    priceUnit: snapshot?.contractPriceUnit ?? editData?.contractSnapshot?.contractPriceUnit ?? '',
    bags: snapshot?.contractBags ?? editData?.contractSnapshot?.contractBags ?? '',
    outrightPrice: snapshot?.contractOutrightPrice ?? editData?.contractSnapshot?.contractOutrightPrice ?? '',
    ptbfDiffs: snapshot?.contractPtbfDiffs ?? editData?.contractSnapshot?.contractPtbfDiffs ?? '',
  }

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle sx={{ pb: 1, pt: 1.25, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography component="span" sx={{ flex: 1, fontWeight: 700, fontSize: '1rem' }}>
          Sale Contract - Amendment
        </Typography>
        <Stack direction="row" spacing={0.5}>
          <Button variant="contained" size="small" color="success" onClick={handleSave} disabled={isBusy}>
            {isBusy ? <CircularProgress size={14} color="inherit" /> : 'Save'}
          </Button>
          <Button variant="outlined" size="small" onClick={handleCancel}>
            Cancel
          </Button>
        </Stack>
      </DialogTitle>
      <Divider />
      <DialogContent dividers sx={{ pt: 1.5 }}>
        {validationError && (
          <Alert severity="error" sx={{ mb: 1.5 }}>
            {validationError}
          </Alert>
        )}

        {/* ── 1) Key fields ── */}
        <Grid container spacing={1.5}>
          <Grid item xs={12} sm={6}>
            {mode === 'edit' ? (
              <TextField
                label="Contract No"
                size="small"
                fullWidth
                value={form.contractNo}
                InputProps={{ readOnly: true }}
                sx={roSx}
              />
            ) : (
              <Autocomplete
                size="small"
                options={contractItems}
                loading={contractsLoading}
                getOptionLabel={(o: LookupItem | string) => (typeof o === 'string' ? o : o.label)}
                isOptionEqualToValue={(o, v) => o.value === v.value}
                inputValue={form.contractNo}
                onInputChange={(_, value, reason) => {
                  if (reason === 'input') {
                    set('contractNo', value)
                    fetchContracts(value)
                  } else if (reason === 'clear') {
                    set('contractNo', '')
                  }
                }}
                onChange={(_, item) => set('contractNo', item?.value ?? '')}
                filterOptions={(x) => x}
                renderInput={(params) => <TextField {...params} label="Contract No" />}
              />
            )}
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Amendment No"
              size="small"
              fullWidth
              value={form.amendmentNo}
              onChange={(e) => set('amendmentNo', e.target.value)}
              InputProps={{ readOnly: mode === 'edit' }}
              sx={mode === 'edit' ? roSx : undefined}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Amendment Date"
              type="date"
              size="small"
              fullWidth
              value={form.amendmentDate}
              onChange={(e) => set('amendmentDate', e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
        </Grid>

        {/* ── 2) Contract snapshot context ── */}
        <Box sx={{ mt: 2, mb: 0.5 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: 0.5, textTransform: 'uppercase', fontSize: '0.68rem' }}>
            Contract Reference
          </Typography>
          <Divider sx={{ mt: 0.5 }} />
        </Box>
        <Grid container spacing={1.5} sx={{ mt: 0 }}>
          <Grid item xs={12} sm={6}>
            <TextField label="Vendor" size="small" fullWidth value={ctx.vendor} InputProps={{ readOnly: true }} InputLabelProps={{ shrink: true }} sx={roSx} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="Contract Date" size="small" fullWidth value={ctx.contractDate} InputProps={{ readOnly: true }} InputLabelProps={{ shrink: true }} sx={roSx} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField label="Contract Type" size="small" fullWidth value={ctx.contractType} InputProps={{ readOnly: true }} InputLabelProps={{ shrink: true }} sx={roSx} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField label="Quality" size="small" fullWidth value={ctx.quality} InputProps={{ readOnly: true }} InputLabelProps={{ shrink: true }} sx={roSx} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField label="No of bags" size="small" fullWidth value={ctx.bags} InputProps={{ readOnly: true }} InputLabelProps={{ shrink: true }} sx={roSx} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField label="Contract Quantity" size="small" fullWidth value={ctx.contractQty} InputProps={{ readOnly: true }} InputLabelProps={{ shrink: true }} sx={roSx} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField label="Price Unit" size="small" fullWidth value={ctx.priceUnit} InputProps={{ readOnly: true }} InputLabelProps={{ shrink: true }} sx={roSx} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField label="Outright Price" size="small" fullWidth value={ctx.outrightPrice} InputProps={{ readOnly: true }} InputLabelProps={{ shrink: true }} sx={roSx} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField label="PTBF Diffs" size="small" fullWidth value={ctx.ptbfDiffs} InputProps={{ readOnly: true }} InputLabelProps={{ shrink: true }} sx={roSx} />
          </Grid>
        </Grid>

        {/* ── 3) Editable amendment details ── */}
        <Box sx={{ mt: 2, mb: 0.5 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: 0.5, textTransform: 'uppercase', fontSize: '0.68rem' }}>
            Amendment Details
          </Typography>
          <Divider sx={{ mt: 0.5 }} />
        </Box>
        <Grid container spacing={1.5} sx={{ mt: 0 }}>
          <Grid item xs={12}>
            <TextField
              label="Content Before Change"
              size="small"
              fullWidth
              multiline
              minRows={3}
              value={form.contentBeforeChange}
              onChange={(e) => set('contentBeforeChange', e.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Content After Change"
              size="small"
              fullWidth
              multiline
              minRows={3}
              value={form.contentAfterChange}
              onChange={(e) => set('contentAfterChange', e.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Note"
              size="small"
              fullWidth
              multiline
              minRows={2}
              value={form.note}
              onChange={(e) => set('note', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Price"
              type="number"
              size="small"
              fullWidth
              value={form.price}
              onChange={(e) => setNum('price', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Quantity"
              type="number"
              size="small"
              fullWidth
              value={form.quantity}
              onChange={(e) => setNum('quantity', e.target.value)}
            />
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  )
}
